import React, {
  RefObject,
  useMemo,
  useState,
  useCallback,
  SyntheticEvent,
  useEffect,
} from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import clsx from 'clsx';
import defer from 'lodash/defer';
import debounce from 'lodash/debounce';
import values from 'lodash/values';
import isNil from 'lodash/isNil';
import { getAddressString, isEmptyString } from 'utils/helper';
import AutoCompletedInput from 'components/common/autoCompletedInput';
import { PayerEntryDTO } from 'artifacts/dtos';
import { useGetFavoriteInsurances, useSearchInsuranceByOrg } from 'hooks/useApi';
import InputText from 'components/common/InputText';
import compact from 'lodash/compact';
import Wrapper from 'components/common/Wrapper';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { NullableCommunique } from 'types/types';
import { FilterOptionsState } from '@mui/material/useAutocomplete';
import { useQueryClient } from '@tanstack/react-query';

type Props = {
  className?: string;
  id: string;
  value?: string | null;
  label?: string;
  placeholder: string;
  readonly?: boolean;
  onChange: (value: PayerEntryDTO | null) => void;
  hideDateOfBirth?: boolean;
  inputRef?: RefObject<HTMLInputElement>;
  resetOnEmpty?: boolean;
  required?: boolean;
  error?: boolean;
  showFavorites?: boolean;
};

export default function InsuranceLookup(props: Props) {
  const {
    className,
    id,
    onChange,
    placeholder,
    value: _value,
    readonly,
    inputRef,
    error = false,
    label,
    showFavorites = false,
  } = props;
  const communiqueData = useSelector(
    (state: RootState) => state.communique,
  ) as NullableCommunique;

  const [inputValue, setInputValue] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const containerClasses = clsx('flex flex-wrap flex-row w-full space-x-4 mb-3', className);
  const value = { payerName: _value ?? '' };

  const enabled = open && !isEmptyString(inputValue) && !showFavorites;
  const { data: _options = [], isFetching: isLoading } = useSearchInsuranceByOrg(
    { organizationId: communiqueData?.organizationId, search: inputValue },
    enabled,
  );

  const { data: _favorites = [] } = useGetFavoriteInsurances(
    Number(communiqueData?.organizationId),
  );

  const options = useMemo(() => (
    _options.map((option) => ({
      ...option,
      claimType: option?.insuranceType,
    }))
  ), [_options]);

  const favorites = useMemo(() => (
    _favorites.map((option) => ({
      ...option,
      claimType: option?.insuranceType,
    }))
  ), [_favorites]);

  const handleChange = (value: PayerEntryDTO | PayerEntryDTO[]) => {
    setOpen(false);
    onChange(value as PayerEntryDTO);
  };

  const handleInputChange = useCallback(debounce((
    event: SyntheticEvent,
    newInputValue: string,
  ) => {
    if (open) {
      setInputValue(newInputValue);
    }
  }, 500), [open]);

  const noOptionsText = useMemo(() => (
    options.length === 0 && inputValue === ''
      ? 'Start typing to see the list'
      : 'No Insurance Found'
  ), [options, inputValue]);

  const isEmpty = useMemo(() => (
    isNil(value) || values(value).every(isEmptyString)
  ), [value]);

  useEffect(() => {
    defer(() => { inputRef?.current?.focus(); });
    if (!isEmptyString(value?.payerName)) {
      setInputValue(value.payerName);
    }
  }, []);

  const classes = clsx('w-full py-0 rounded', {
    'border-gray-200': !error,
    'border-red-500': error,
  });

  const filterOptionsFromList = (
    options: PayerEntryDTO[],
    state: FilterOptionsState<PayerEntryDTO>,
  ) => {
    const searchValue = isEmptyString(state.inputValue) ? inputValue : state.inputValue;
    const searchRegex = new RegExp(searchValue.replace(/\\/g, '\\\\'), 'i');
    return isEmptyString(searchValue) ? options : options.filter(({ payerName, searchTerms }) => (
      searchRegex.test(payerName ?? '') || searchRegex.test(searchTerms ?? '')
    ));
  };

  return (
    <div className={containerClasses}>
      <Wrapper
        id="patient-select"
        className={classes}
        label={label}
        error={error}
        showErrorText={false}
        labelClasses="font-semibold text-xs text-gray-500"
      >
        {readonly
          ? (
            <InputText
              id={id}
              onChange={() => {}}
              className="w-screen"
              disabled
              value={value?.payerName ?? undefined}
            />
          ) : (
            <AutoCompletedInput<PayerEntryDTO>
              id={id}
              blurOnSelect
              disabled={readonly}
              options={(showFavorites ? favorites : options) ?? []}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
              open={open}
              getLabel={(option) => option?.payerName ?? '--'}
              renderOption={(props: Record<string, any>, option: PayerEntryDTO) => (
                <div
                  {...props}
                  className={clsx(
                    props?.className,
                    'flex flex-col !items-start !justify-start !w-full !text-left gap-2 text-xs',
                  )}
                >
                  <span className="font-semibold">
                    {option?.payerName ?? '--'}
                  </span>
                  <span className="flex gap-2 populate-vertical-list-text">
                    {compact([
                      compact([option.payerId, option.isPaper ? ' (Paper)' : null]).join(' ') || '--',
                    ]).map((item: string) => (
                      <span key={item} className="populate-list-item">{item}</span>
                    ))}
                  </span>
                  <span className="populate-list-item">{option.insuranceType}</span>
                  {option.address && (
                  <span>
                    {getAddressString(option.address)}
                  </span>
                  )}
                </div>
              )}
              loading={isLoading}
              inputRef={inputRef}
              defaultValue={value}
              openOnFocus
              includeInputInList
              className="w-full"
              onChange={handleChange}
              filterOptions={showFavorites ? filterOptionsFromList : (x) => x}
              placeholder={placeholder}
              onInputChange={handleInputChange}
              clearOnBlur={false}
              noOptionsText={noOptionsText}
              dropdownClassName={clsx('!overflow-hidden', {
                '!p-0': options?.length && isEmpty && !isLoading,
              })}
              popupIcon={isLoading ? <CircularProgress size={14} /> : undefined}
            />
          )}
      </Wrapper>
    </div>
  );
}
